import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormTextarea,
  TabContent,
  TabList,
  Tabs,
  TabTrigger,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useAddMenuRow } from "@/api/hooks/useEventMenu";
import type { Drink } from "@/api/types/drink";
import { DrinkPickerList } from "@/features/drinks/DrinkPickerDialog";
import {
  draftsToIngredients,
  emptyIngredientDraft,
  type IngredientDraft,
} from "@/features/drinks/ingredientDraft";
import { IngredientsEditor } from "@/features/drinks/IngredientsEditor";

interface AddMenuRowDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  existingDrinkIds: string[];
  nextSortOrder: number;
}

/**
 * "Add drink" for the event menu builder: a Library tab (search + pick an
 * active drink) and an Add custom tab (name/story/ingredients for an
 * event-only drink, per the Phase 4 MenuRow contract's is_custom fields).
 */
export function AddMenuRowDialog({
  open,
  onClose,
  eventId,
  existingDrinkIds,
  nextSortOrder,
}: AddMenuRowDialogProps) {
  const [tab, setTab] = useState<"library" | "custom">("library");
  const [customName, setCustomName] = useState("");
  const [customStory, setCustomStory] = useState("");
  const [customIngredients, setCustomIngredients] = useState<IngredientDraft[]>([
    emptyIngredientDraft(),
  ]);

  const addMenuRow = useAddMenuRow();

  useEffect(() => {
    if (!open) return;
    setTab("library");
    setCustomName("");
    setCustomStory("");
    setCustomIngredients([emptyIngredientDraft()]);
  }, [open]);

  async function handlePickLibraryDrink(drink: Drink) {
    try {
      await addMenuRow.mutateAsync({
        eventId,
        drink_id: drink.id,
        sort_order: nextSortOrder,
      });
      toastSuccess(`${drink.name} added to menu`);
      onClose();
    } catch (err) {
      toastError("Couldn't add drink", getErrorMessage(err));
    }
  }

  async function handleAddCustom() {
    if (!customName.trim()) return;
    try {
      await addMenuRow.mutateAsync({
        eventId,
        custom_name: customName.trim(),
        custom_story: customStory.trim() ? customStory.trim() : null,
        custom_ingredients: draftsToIngredients(customIngredients),
        sort_order: nextSortOrder,
      });
      toastSuccess(`${customName.trim()} added to menu`);
      onClose();
    } catch (err) {
      toastError("Couldn't add custom drink", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Add drink to menu">
      <DialogContent size="lg">
        <DialogHeader onClose={onClose}>Add drink to menu</DialogHeader>
        <DialogBody className="space-y-4">
          <Tabs value={tab} onValueChange={(v) => setTab(v as "library" | "custom")}>
            <TabList>
              <TabTrigger value="library">Library</TabTrigger>
              <TabTrigger value="custom">Add custom</TabTrigger>
            </TabList>

            <TabContent value="library">
              <DrinkPickerList onSelect={(d) => void handlePickLibraryDrink(d)} excludeIds={existingDrinkIds} />
            </TabContent>

            <TabContent value="custom">
              <div className="space-y-4">
                <FormField
                  label="Name"
                  required
                  value={customName}
                  disabled={addMenuRow.isPending}
                  onChange={(e) => setCustomName(e.target.value)}
                />
                <FormTextarea
                  label="Story"
                  value={customStory}
                  disabled={addMenuRow.isPending}
                  onChange={(e) => setCustomStory(e.target.value)}
                  hint="Event-only drink — can be promoted into the library later."
                />
                <IngredientsEditor
                  label="Ingredients"
                  items={customIngredients}
                  onChange={setCustomIngredients}
                  onAdd={() => setCustomIngredients((prev) => [...prev, emptyIngredientDraft()])}
                  disabled={addMenuRow.isPending}
                  testIdPrefix="custom-ingredients"
                />
              </div>
            </TabContent>
          </Tabs>
        </DialogBody>
        {tab === "custom" && (
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={addMenuRow.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleAddCustom()}
              disabled={!customName.trim() || addMenuRow.isPending}
            >
              {addMenuRow.isPending ? "Adding..." : "Add custom drink"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
